import { HStack, Tag, Text } from '@chakra-ui/react';
import {
  ALL_OPTION,
  areOptionsEqualByValue,
  CustomOption,
  isOptionInListByValue,
  removeOptionFromListByValue,
} from '@utils/components/customChipList.utils';
import { memo } from 'react';
import { useIntl } from 'react-intl';

interface CustomChipListProps {
  availableOptions: CustomOption[];
  selectedOptions?: CustomOption[];
  displayOptionAll?: boolean;
  path?: string;
  isDisabled?: boolean;
  onChange?: (value: string[]) => void;
  distinctValues?: string[];
  canUnselectDistinct?: boolean;
  size?: 'md' | 'lg';
}

const CustomChipList = ({
  availableOptions,
  displayOptionAll = false,
  selectedOptions = [],
  path,
  isDisabled = false,
  canUnselectDistinct = false,
  onChange,
  distinctValues,
  size = 'md',
}: Readonly<CustomChipListProps>) => {
  const { formatMessage } = useIntl();
  const all = ALL_OPTION;
  type optionType = CustomOption | typeof all;
  const optionList: optionType[] = displayOptionAll ? [all, ...availableOptions] : availableOptions;

  function uncheckOptionAll(selectedOptionsList: CustomOption[]) {
    if (displayOptionAll && isOptionInListByValue(all, selectedOptionsList)) {
      selectedOptionsList = removeOptionFromListByValue(all, selectedOptionsList);
    }
    return selectedOptionsList;
  }

  const handleTagClick = (clickedOption: optionType) => {
    let newSelectedOptions: optionType[];

    if (distinctValues && distinctValues.includes(clickedOption.value)) {
      if (canUnselectDistinct && isOptionInListByValue(clickedOption, selectedOptions)) {
        newSelectedOptions = selectedOptions.filter((option) => clickedOption.value !== option.value);
      } else {
        // remove other unique options
        newSelectedOptions = [clickedOption, ...selectedOptions.filter((o) => !distinctValues.includes(o.value))];
        newSelectedOptions = uncheckOptionAll(newSelectedOptions);
      }
    } else {
      if (displayOptionAll && areOptionsEqualByValue(clickedOption, all)) {
        //Si on clique sur le bouton all
        newSelectedOptions = [all];
      } else if (isOptionInListByValue(clickedOption, selectedOptions)) {
        //Si la case était déjà cochée
        newSelectedOptions = removeOptionFromListByValue(clickedOption, selectedOptions);
        newSelectedOptions = newSelectedOptions.length === 0 && displayOptionAll ? [all] : newSelectedOptions;
      } else {
        // Si la case n'était pas cochée
        newSelectedOptions = uncheckOptionAll(selectedOptions);
        newSelectedOptions.push(clickedOption);
      }
    }
    onChange?.(newSelectedOptions.map((option) => option.value));
  };

  const getFilterLabel = (option: CustomOption) => {
    if (areOptionsEqualByValue(option, all)) {
      return formatMessage({ id: `filters.all` });
    } else if (path) {
      return formatMessage({ id: `${path}.${option.label}` });
    } else {
      return option.label.replace('_', ' ');
    }
  };

  const { normalOptions, distinctOptions } = Object.values(optionList).reduce(
    ({ normalOptions, distinctOptions }, curr) => {
      if (distinctValues?.includes(curr.value)) {
        return { normalOptions, distinctOptions: distinctOptions.concat(curr) };
      } else {
        return { distinctOptions, normalOptions: normalOptions.concat(curr) };
      }
    },
    {
      normalOptions: [] as CustomOption[],
      distinctOptions: [] as CustomOption[],
    },
  );

  function displayTag(option: CustomOption, isDistinct = false, index?: number) {
    const isClickable = () => {
      if (isDisabled || option.disabled) {
        return false;
      } else if (!isDistinct || canUnselectDistinct) {
        return true;
      } else if (!selectedOptions.some((selectedOption) => selectedOption.value === option.value)) {
        return true;
      }
      return false;
    };

    return (
      <Tag.Root
        onClick={() => isClickable() && handleTagClick(option)}
        key={option.value}
        cursor={isClickable() ? 'pointer' : 'default'}
        data-disabled={isDisabled}
        data-active={isOptionInListByValue(option, selectedOptions)}
        marginLeft={isDistinct && index !== 0 ? '-2px' : 0}
        size={size}
      >
        <Tag.Label data-disabled={isDisabled}>{getFilterLabel(option)}</Tag.Label>
        {option.count !== undefined && (
          <Tag.EndElement data-disabled={isDisabled}>
            <Text fontSize="sm">{option.count}</Text>
          </Tag.EndElement>
        )}
      </Tag.Root>
    );
  }

  return (
    <HStack columnGap={3} rowGap={1} flexWrap="wrap">
      {normalOptions.length > 0 && (
        <HStack gap={1} flexWrap="wrap">
          {normalOptions.map((tag, index) => displayTag(tag, false, index))}
        </HStack>
      )}
      {distinctOptions.length > 0 && (
        <HStack gap={0}>{distinctOptions.map((tag, index) => displayTag(tag, true, index))}</HStack>
      )}
    </HStack>
  );
};

export default memo(CustomChipList);
